import { useVideoStream } from '../../hooks/useVideoStream';
import type { VideoState } from '../../types/videoStream';

import { ErrorView, LoadingView, PlaceholderView } from './StatusViews';
import { StreamingView } from './StreamingView';

export interface BehaviorVideoProps {
    cageId: number;
    bioscopeId?: number;
    cageName?: string;
}

type ViewState = 'idle' | 'loading' | 'error' | 'streaming';

const STREAM_DURATION_SECONDS = 300;

export default function BehaviorVideo(props: BehaviorVideoProps) {
    const { cageId, bioscopeId, cageName } = props;

    const { videoState, controlState, startStream, stopStream } = useVideoStream({
        cageId,
        bioscopeId,
    });

    // Resolve current view state
    const viewState = resolveViewState(videoState, bioscopeId);

    // Render appropriate view based on state
    return (
        <>
            {viewState === 'idle' && (
                <PlaceholderView
                    cageName={cageName}
                    controlState={controlState}
                    onStart={startStream}
                />
            )}
            {viewState === 'loading' && <LoadingView cageName={cageName} onStop={stopStream} />}
            {viewState === 'error' && (
                <ErrorView cageName={cageName} onRetry={startStream} onStop={stopStream} />
            )}
            {viewState === 'streaming' && (
                <StreamingView
                    cageId={cageId}
                    cageName={cageName}
                    videoState={videoState}
                    streamDuration={STREAM_DURATION_SECONDS}
                    onStop={stopStream}
                />
            )}
        </>
    );
}

function resolveViewState(videoState: VideoState, bioscopeId?: number): ViewState {
    if (!bioscopeId || videoState.status === 'disabled') return 'idle';

    if (videoState.status === 'error' || videoState.status === 'disconnected') return 'error';

    if (videoState.status === 'active' && videoState.playbackId) return 'streaming';

    return 'loading';
}
